import React, { useRef } from 'react';
import ThumbnailList from './ThumbnailList';
import GalleryContent from './GalleryContent';
import ExpandedModal from './ExpandedModal/ExpandedModal';
import {
  GalleryContainer,
  ScrollThumbsDownButton,
  ScrollThumbsUpButton,
  ModalContainer,
  ThumbnailListBackground
} from './Styles';
import ModalThumbnailList from './ExpandedModal/ModalThumbnailList';
import ModalContent from './ExpandedModal/ModalContent';
import useGalleryState from '../../hooks/useGalleryState';
import SkeletonLoader from './SkeletonLoader/SkeletonLoader';
import DirectionalArrow from '../Icons/DirectionalArrow';
import CountBadge from '../Product/CountBadge';
import ProductBadge from '../Product/ProductBadge';
import { StyleSheetManager } from 'styled-components';

const ProductGallery: React.FC = () => {
  const thumbnailListRef = useRef<HTMLDivElement>(null);

  const shouldForwardProp = (prop: string): boolean =>
    ![
      'paddingTopPercentage',
      'widthPercentage',
      'modalImageMaxHeight',
      'mobileMaxHeight',
      'isModal',
      'mobileDisplay',
      'siteId',
      'mobileZoomRate',
      'zoomRate',
      'mediaItemsLength',
    ].includes(prop);

  const {
    loading,
    selectedMediaIndex,
    modalSelectedImageIndex,
    videoIndex,
    modalVisible,
    mediaItems,
    modalImageMaxHeight,
    iframeDimensions,
    showThreeSixtyToggle,
    showThreeSixtyOverlay,
    showDownArrow,
    showUpArrow,
    siteId,
    selectedVariantData,
    toggleShow360View,
    handleThumbnailClick,
    handleGalleryScroll,
    handleThumbnailScroll,
    handleGalleryClick,
    handleModalThumbnailClick,
    handleModalGalleryScroll
  } = useGalleryState(thumbnailListRef);

  if (loading) {
    return <SkeletonLoader />;
  }

  const videoThumbnail = selectedVariantData?.variantMedia?.videos?.[0]?.altText || '';

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      {modalVisible ? (
        <ModalContainer onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
          <ExpandedModal toggleShowModal={handleGalleryClick} modalVisible={modalVisible}>
            <CountBadge modalSelectedImageIndex={modalSelectedImageIndex} mediaItemsLength={mediaItems.length} />
            <ModalContent
              siteId={siteId}
              videoIndex={videoIndex}
              onGalleryScroll={handleModalGalleryScroll}
              mediaItems={mediaItems}
              videoThumbnail={videoThumbnail}
              selectedImageIndex={modalSelectedImageIndex}
              setSelectedIndex={handleModalThumbnailClick}
              modalImageMaxHeight={modalImageMaxHeight}
              iframeDimensions={iframeDimensions}
            />
          </ExpandedModal>
          <ModalThumbnailList
            siteId={siteId}
            images={selectedVariantData?.variantMedia?.images || []}
            onThumbnailClick={handleModalThumbnailClick}
            videoThumbnail={mediaItems[videoIndex]?.thumbUrl}
            videoIndex={videoIndex}
            activeIndex={modalSelectedImageIndex}
            id="image-gallery-modal-thumblist"
          />
        </ModalContainer>
      ) : (
        <div style={{ position: 'relative', maxWidth: '850px', margin: '0 auto' }}>
          <GalleryContainer onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
            <ThumbnailListBackground />
            <ThumbnailList
              siteId={siteId}
              mediaItems={mediaItems}
              ref={thumbnailListRef}
              onThumbnailClick={handleThumbnailClick}
              videoThumbnail={mediaItems[videoIndex]?.thumbUrl || ''}
              activeIndex={selectedMediaIndex}
              mediaItemsLength={mediaItems.length}
            />
            <GalleryContent
              showThreeSixtyOverlay={showThreeSixtyOverlay}
              showThreeSixtyToggle={!!showThreeSixtyToggle}
              onGalleryScroll={handleGalleryScroll}
              mediaItems={mediaItems}
              videoThumbnail={mediaItems[videoIndex]?.thumbUrl}
              selectedImageIndex={selectedMediaIndex}
              toggleShowModal={handleGalleryClick}
              setSelectedIndex={handleThumbnailClick}
              videoIndex={videoIndex}
              threeSixtyViewURL={selectedVariantData?.threesixtyVideo}
              toggleShow360View={toggleShow360View}
              siteId={siteId}
            />
            {showUpArrow && mediaItems.length > 5 && (
              <ScrollThumbsUpButton className="gallery-button" onClick={() => handleThumbnailScroll(-1)}>
                <DirectionalArrow direction="up" fill="#1A1B1B" strokeWidth={1.5} width="12" height="7" />
              </ScrollThumbsUpButton>
            )}
            {showDownArrow && mediaItems.length > 5 && (
              <ScrollThumbsDownButton className="gallery-button" onClick={() => handleThumbnailScroll(1)}>
                <DirectionalArrow direction="down" fill="#1A1B1B" strokeWidth={1.5} width="12" height="7" />
              </ScrollThumbsDownButton>
            )}
          </GalleryContainer>
        </div>
      )}
    </StyleSheetManager>
  );
};

export default ProductGallery;
